import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type { OpsOverview } from '@popcart/core/v1';
import { api } from '../api';
import { ErrorState, LineDot, Loading, Segmented, useNow, useTitle } from '../ui/bits';
import { Icon, type IconName } from '../ui/Icon';
import { DotMeter, Donut, Fig, Popover, Ring, Sheet, Spark, type Slice } from '../ui/viz';
import { count, minutes, peso, percent, updatedAgo } from '../lib/format.js';
import { boardShopNames, filterBoard, lateCount, normaliseBoard } from '../lib/board.js';
import { STATE_ART } from '../lib/voice.js';
import { useOpsBoard, useOpsOverview, useOpsRiders, useOpsShops, type OpsRange, type OpsRider, type OpsShop } from './queries';
import { Alerts, BoardFilters, LiveBoard, RidersMap, shopStatusView } from './parts';
import { reportKey } from './reports/lib.js';
import { dayLabel, lastNDays, shortDay } from './reports/period.js';

const RANGES: { value: OpsRange; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
];
const VS: Record<OpsRange, string> = { today: 'vs last week', '7d': 'vs previous 7 days', '30d': 'vs previous 30 days' };
/** Days of trend behind the hero line: today shows the last two weeks for context. */
const TREND_DAYS: Record<OpsRange, number> = { today: 14, '7d': 7, '30d': 30 };
const STAGES: { stage: 'new' | 'preparing' | 'ready' | 'on_the_way'; label: string; icon: IconName }[] = [
  { stage: 'new', label: 'New', icon: 'receipt' },
  { stage: 'preparing', label: 'Preparing', icon: 'flame' },
  { stage: 'ready', label: 'Ready', icon: 'bag' },
  { stage: 'on_the_way', label: 'On the way', icon: 'pin' },
];
const LINE_NAME: Record<string, string> = { fresh: 'Fresh', grill: 'Grill', mart: 'Grocery', express_surcharge: 'Express' };
const SWATCH: Record<string, string> = { fresh: 'fresh', grill: 'grill', mart: 'mart', express_surcharge: 'express' };

type SheetKey = 'sales' | 'orders' | 'ontime' | 'riders' | 'cash' | 'alerts' | null;

function onTimeTone(p: number | null): 'good' | 'warn' | 'bad' {
  if (p === null) return 'good';
  return p >= 90 ? 'good' : p >= 75 ? 'warn' : 'bad';
}

function shopDot(s: OpsShop) {
  const t = shopStatusView(s).tone;
  return t === 'success' ? 'ok' : t === 'warning' ? 'warn' : t === 'error' ? 'bad' : 'off';
}

/** A stat tile: label, figure, a visual on the right; the whole tile opens its sheet. */
function Tile({ label, children, visual, sub, onOpen, i }: { label: string; children: ReactNode; visual?: ReactNode; sub?: ReactNode; onOpen(): void; i: number }) {
  return (
    <section className="card dv-tile dv-rise" style={{ ['--i' as string]: i }} onClick={onOpen} aria-label={label}>
      <div className="dv-tile-top">
        <span className="dv-label">{label}</span>
        <button
          type="button"
          className="dv-open press"
          aria-label={`Open ${label}`}
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
        >
          <Icon name="chevronRight" />
        </button>
      </div>
      <div className="dv-tile-body">
        <div className="stack s4" style={{ minWidth: 0 }}>
          {children}
          {sub ? <span className="dv-sub">{sub}</span> : null}
        </div>
        {visual}
      </div>
    </section>
  );
}

export function OpsOverviewPage() {
  useTitle('Ops overview');
  const [range, setRange] = useState<OpsRange>('today');
  const [sheet, setSheet] = useState<SheetKey>(null);
  const [shop, setShop] = useState<string | null>(null);
  const [lateOnly, setLateOnly] = useState(false);
  const overview = useOpsOverview(range);
  const board = useOpsBoard();
  const shops = useOpsShops();
  const riders = useOpsRiders();
  const health = useQuery({ queryKey: ['ops', 'map-health'], queryFn: () => api.mapHealth(), refetchInterval: 60_000 });
  const period = useMemo(() => lastNDays(TREND_DAYS[range]), [range]);
  const trend = useQuery({ queryKey: reportKey(period), queryFn: () => api.opsReport(period), refetchInterval: 60_000 });
  const now = useNow(1000);

  const norm = useMemo(() => normaliseBoard(board.data), [board.data]);
  const view = useMemo(() => filterBoard(norm, { shop, lateOnly }), [norm, shop, lateOnly]);
  const k = overview.data?.kpis;
  const alerts = overview.data?.alerts ?? [];
  const late = lateCount(norm);
  const riderList = riders.data ?? [];
  const online = riderList.filter((r) => r.online);
  const busy = online.filter((r) => r.activeDeliveries > 0).length;
  const days = trend.data?.days ?? [];
  const ago = board.dataUpdatedAt ? Math.max(0, Math.round((now - board.dataUpdatedAt) / 1000)) : null;
  const h = health.data;
  const healthTone = !h ? 'warn' : h.routing.status !== 'ok' ? 'bad' : h.basemap?.source === 'self-hosted' && h.fonts === 'self-hosted' ? 'ok' : 'warn';

  const tickets = useMemo(
    () =>
      norm
        .filter((c) => c.stage !== 'delivered')
        .flatMap((c) => c.orders.map((o) => ({ ...o, stage: c.stage, stageLabel: c.label })))
        .sort((a, b) => Number(b.late) - Number(a.late) || b.minutes - a.minutes),
    [norm],
  );

  const slices: Slice[] = (overview.data?.salesByLine ?? []).map((r) => ({ key: r.line, label: LINE_NAME[r.line] ?? r.line, value: r.centavos, swatch: SWATCH[r.line] ?? 'other' }));
  const salesTotal = slices.reduce((s, x) => s + Math.max(0, x.value), 0);
  const shopList = (shops.data ?? []).slice().sort((a, b) => b.salesTodayCentavos - a.salesTodayCentavos || b.ordersToday - a.ordersToday);
  const topShopSales = Math.max(1, ...shopList.map((s) => s.salesTodayCentavos));
  const delta = k?.ordersDeltaPct ?? null;

  return (
    <>
      <header className="dv-head">
        <div className="stack s4">
          <h1>Overview</h1>
          <span className="dv-live" aria-live="off">
            <span className="dv-pulse" aria-hidden="true" />
            Live{ago !== null ? ` · ${ago}s` : ''}
          </span>
        </div>
        <div className="dv-actions">
          <button type="button" className="dv-ibtn press" aria-label={alerts.length ? `Needs you: ${alerts.length}` : 'Needs you: nothing'} onClick={() => setSheet('alerts')}>
            <Icon name="bell" />
            {alerts.length ? (
              <span className="badge-n" aria-hidden="true">
                {alerts.length}
              </span>
            ) : null}
          </button>
          <Popover
            label="System health"
            trigger={({ open, toggle, id }) => (
              <button type="button" className="dv-ibtn press" aria-expanded={open} aria-controls={open ? id : undefined} aria-label="Maps and routing health" onClick={toggle}>
                <Icon name="globe" />
                <span className={`health ${healthTone}`} aria-hidden="true" />
              </button>
            )}
          >
            <HealthRows h={h} error={health.isError} />
          </Popover>
          <Segmented<OpsRange> label="Time range" value={range} options={RANGES} onChange={setRange} />
        </div>
      </header>

      {overview.isError ? (
        <section className="card">
          <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />
        </section>
      ) : !k ? (
        <div className="dv-grid">
          <div className="skeleton dv-span-5" style={{ minHeight: 340 }} aria-hidden="true" />
          <div className="dv-span-7 dv-tiles">
            {Array.from({ length: 4 }, (_, i) => (
              <div key={i} className="skeleton" style={{ minHeight: 162 }} aria-hidden="true" />
            ))}
          </div>
        </div>
      ) : (
        <div className="dv-grid">
          {/* Hero: sales for the range, the trend line, two quiet supporting figures. */}
          <section className="dv-hero dv-span-5 dv-rise" aria-label="Gross sales" style={{ ['--i' as string]: 0 }}>
            <div className="dv-hero-top">
              <div className="stack s12">
                <span className="dv-label">Gross sales</span>
                <Fig text={peso(k.salesCentavos)} size="xl" />
                <span className="row">
                  {delta === null ? (
                    <span className="dv-delta flat">New</span>
                  ) : (
                    <span className={`dv-delta ${delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat'}`} title={`Orders ${VS[range]}`}>
                      {delta > 0 ? '▲' : delta < 0 ? '▼' : '•'} {Math.abs(Math.round(delta))}%
                    </span>
                  )}
                </span>
              </div>
              <button type="button" className="dv-ibtn press" aria-label="Open sales" onClick={() => setSheet('sales')}>
                <Icon name="chart" />
              </button>
            </div>
            {days.length ? (
              <Spark
                values={days.map((d) => d.gmvCentavos)}
                labels={days.map((d) => dayLabel(d.day))}
                format={peso}
                ariaLabel={`Sales per day, ${shortDay(days[0]?.day ?? '')} to ${shortDay(days[days.length - 1]?.day ?? '')}`}
              />
            ) : (
              <div className="dv-spark" aria-hidden="true" />
            )}
            <div className="dv-hero-foot">
              <div>
                <span className="dv-label">Avg order</span>
                <Fig text={k.orders ? peso(Math.round(k.salesCentavos / k.orders)) : '—'} size="sm" />
              </div>
              <div>
                <span className="dv-label">Catch-weight</span>
                <Fig text={peso(k.catchWeightNetCentavos)} size="sm" />
              </div>
            </div>
          </section>

          <div className="dv-span-7 dv-tiles">
            <Tile label="Orders" i={1} onOpen={() => setSheet('orders')} sub={late ? <span className="dv-chip bad">{late} late</span> : undefined} visual={<MiniBars values={days.map((d) => d.orders)} />}>
              <Fig text={count(k.orders)} size="lg" />
            </Tile>
            <Tile label="On time" i={2} onOpen={() => setSheet('ontime')} sub={k.avgDeliveryMinutes === null ? undefined : minutes(k.avgDeliveryMinutes)} visual={<Ring pct={k.onTimePct} tone={onTimeTone(k.onTimePct)} />}>
              <Fig text={percent(k.onTimePct)} size="lg" />
            </Tile>
            <Tile label="Riders online" i={3} onOpen={() => setSheet('riders')} sub={busy ? `${busy} on a delivery` : undefined} visual={<DotMeter total={k.ridersTotal} on={k.ridersOnline} busy={busy} />}>
              <Fig text={`${k.ridersOnline} / ${k.ridersTotal}`} size="lg" />
            </Tile>
            <Tile label="Cash on the road" i={4} onOpen={() => setSheet('cash')} visual={<span className="dv-disc" aria-hidden="true"><Icon name="cash" /></span>}>
              <Fig text={peso(k.codOnRoadCentavos)} size="lg" />
            </Tile>
          </div>

          {/* Live pipeline */}
          <section className="card dv-card dv-span-8 dv-rise" aria-labelledby="pipe-h" style={{ ['--i' as string]: 5 }}>
            <div className="dv-card-h">
              <h2 id="pipe-h">Live orders</h2>
              {late ? <span className="dv-chip bad">{late} late</span> : null}
              <span className="grow" />
              <Link to="/ops/live" className="dv-link">
                Board <Icon name="chevronRight" />
              </Link>
            </div>
            {board.isError ? (
              <ErrorState error={board.error} onRetry={() => void board.refetch()} />
            ) : board.isLoading ? (
              <Loading label="Loading live orders" />
            ) : (
              <>
                <div className="dv-pipe" role="list" aria-label="Orders by stage">
                  {STAGES.map((s) => {
                    const col = norm.find((c) => c.stage === s.stage);
                    const n = col?.count ?? 0;
                    const anyLate = col?.orders.some((o) => o.late) ?? false;
                    return (
                      <div key={s.stage} role="listitem" className={`dv-stage${n ? ' has' : ''}${anyLate ? ' late' : ''}`} aria-label={`${s.label}: ${n}`}>
                        <span className="node" aria-hidden="true">
                          <Icon name={s.icon} />
                        </span>
                        <Fig text={count(n)} size="md" />
                        <span className="dv-sub">{s.label}</span>
                      </div>
                    );
                  })}
                </div>
                {tickets.length ? (
                  <div className="dv-strip" aria-label="Active orders">
                    {tickets.slice(0, 12).map((o) => (
                      <Link key={o.orderId} to="/ops/live" className={o.late ? 'dv-ticket late' : 'dv-ticket'} aria-label={`${o.code}, ${o.stageLabel}${o.late ? ', late' : ''}`}>
                        <span className="row">
                          <span className="code">{o.code}</span>
                          {o.late ? <span className="dv-chip bad">+{o.lateBy ?? 0}m</span> : <span className="dv-sub">{o.minutes}m</span>}
                        </span>
                        <span className="row">
                          <span className="dv-lines">
                            {o.lines.map((l) => (
                              <LineDot key={l} line={l} />
                            ))}
                            {o.speed === 'express' ? <Icon name="bolt" size="sm" /> : null}
                          </span>
                          <Fig text={peso(o.totalCentavos)} size="xs" animate={false} />
                        </span>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="dv-quiet">
                    <Icon name="check" />
                    All clear
                  </div>
                )}
              </>
            )}
          </section>

          {/* Sales mix */}
          <section className="card dv-card dv-span-4 dv-rise" aria-labelledby="mix-h" style={{ ['--i' as string]: 6 }}>
            <div className="dv-card-h">
              <h2 id="mix-h">Sales mix</h2>
            </div>
            {slices.length ? <Donut slices={slices} total={salesTotal} format={peso} centerLabel="Total" /> : <div className="dv-quiet">No sales yet</div>}
          </section>

          {/* Shops */}
          <section className="card dv-card dv-span-8 dv-rise" aria-labelledby="shops-h" style={{ ['--i' as string]: 7 }}>
            <div className="dv-card-h">
              <h2 id="shops-h">Shops</h2>
              <span className="dv-chip">{shopList.filter((s) => s.status === 'live').length} live</span>
              <span className="grow" />
              <Link to="/ops/shops" className="dv-link">
                All <Icon name="chevronRight" />
              </Link>
            </div>
            {shops.isError ? (
              <ErrorState error={shops.error} onRetry={() => void shops.refetch()} />
            ) : !shops.data ? (
              <Loading label="Loading shops" />
            ) : (
              <div className="dv-shops">
                {shopList.slice(0, 6).map((s) => (
                  <Link key={s.id} to="/ops/shops" className="dv-shop" aria-label={`${s.name}, ${shopStatusView(s).label}, ${count(s.ordersToday)} orders, ${peso(s.salesTodayCentavos)} today`}>
                    <span className={`st ${shopDot(s)}`} title={shopStatusView(s).label} />
                    <span className="nm">{s.name}</span>
                    <span className="dv-lines" aria-hidden="true">
                      {s.lines.map((l) => (
                        <LineDot key={l} line={l} />
                      ))}
                    </span>
                    <span className="dv-share" aria-hidden="true">
                      <i style={{ width: `${Math.round((s.salesTodayCentavos / topShopSales) * 100)}%` }} />
                    </span>
                    <span className="num">
                      <Fig text={count(s.ordersToday)} size="xs" animate={false} />
                    </span>
                    <span className="num">
                      <Fig text={peso(s.salesTodayCentavos)} size="sm" animate={false} />
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </section>

          {/* Riders */}
          <section className="card dv-card dv-riders dv-span-4 dv-rise" aria-labelledby="riders-h" style={{ ['--i' as string]: 8 }}>
            <div className="dv-card-h">
              <h2 id="riders-h">Riders</h2>
              <span className="dv-chip">{online.length} online</span>
              <span className="grow" />
              <Link to="/ops/riders" className="dv-link">
                Dispatch <Icon name="chevronRight" />
              </Link>
            </div>
            {riders.isError ? (
              <ErrorState error={riders.error} />
            ) : !riders.data ? (
              <Loading label="Loading riders" />
            ) : online.length ? (
              <RidersMap riders={riderList} />
            ) : (
              <div className="dv-riders-empty">
                <img src={`/illustrations/${STATE_ART.noRiders}.svg`} alt="" width={88} height={88} />
                <span className="dv-sub">Nobody online yet</span>
              </div>
            )}
          </section>
        </div>
      )}

      {/* Sheets */}
      <Sheet open={sheet === 'sales'} title="Sales" onClose={() => setSheet(null)}>
        <div className="dv-kv">
          <div>
            <span className="dv-label">Gross sales</span>
            <Fig text={peso(k?.salesCentavos ?? 0)} size="md" />
          </div>
          <div>
            <span className="dv-label">Avg order</span>
            <Fig text={k?.orders ? peso(Math.round(k.salesCentavos / k.orders)) : '—'} size="md" />
          </div>
        </div>
        <section className="card dv-card">
          <div className="dv-card-h">
            <h2>Per day</h2>
          </div>
          <DayBars days={days} />
        </section>
        <section className="card dv-card">
          {slices.length ? <Donut slices={slices} total={salesTotal} format={peso} centerLabel="Total" /> : <div className="dv-quiet">No sales yet</div>}
        </section>
        <Link to="/ops/reports" className="btn btn-quiet">
          Reports <Icon name="chevronRight" />
        </Link>
      </Sheet>

      <Sheet open={sheet === 'orders'} title="Orders" onClose={() => setSheet(null)}>
        <div className="dv-kv">
          <div>
            <span className="dv-label">Orders</span>
            <Fig text={count(k?.orders ?? 0)} size="md" />
          </div>
          <div>
            <span className="dv-label">{VS[range].replace(/^vs /, 'vs ')}</span>
            <Fig text={delta === null ? '—' : `${delta >= 0 ? '' : '−'}${Math.abs(Math.round(delta))}%`} size="md" />
          </div>
        </div>
        <section className="card dv-card">
          <div className="dv-card-h">
            <h2>Live now</h2>
            <span className="grow" />
          </div>
          <BoardFilters shopNames={boardShopNames(norm)} shop={shop} setShop={setShop} lateOnly={lateOnly} setLateOnly={setLateOnly} late={late} />
          <LiveBoard board={view} limit={3} />
        </section>
        <Link to="/ops/live" className="btn btn-primary">
          Open the board
        </Link>
      </Sheet>

      <Sheet open={sheet === 'ontime'} title="On time" onClose={() => setSheet(null)}>
        <div className="dv-kv">
          <div>
            <span className="dv-label">On time</span>
            <Fig text={percent(k?.onTimePct ?? null)} size="md" />
          </div>
          <div>
            <span className="dv-label">Door to door</span>
            <Fig text={minutes(k?.avgDeliveryMinutes ?? null)} size="md" />
          </div>
        </div>
        <section className="card dv-card" style={{ alignItems: 'center' }}>
          <Ring pct={k?.onTimePct ?? null} tone={onTimeTone(k?.onTimePct ?? null)}>
            <Fig text={percent(k?.onTimePct ?? null)} size="sm" animate={false} />
          </Ring>
          <span className="dv-sub">{late ? `${late} running late now` : 'Nothing late right now'}</span>
        </section>
      </Sheet>

      <Sheet open={sheet === 'riders'} title="Riders" onClose={() => setSheet(null)}>
        <div className="dv-kv">
          <div>
            <span className="dv-label">Online</span>
            <Fig text={`${online.length} / ${riderList.length}`} size="md" />
          </div>
          <div>
            <span className="dv-label">On a delivery</span>
            <Fig text={count(busy)} size="md" />
          </div>
        </div>
        <section className="card dv-card">
          <RidersMap riders={riderList} quietEmpty />
        </section>
        <RiderRows riders={riderList} value={(r) => (r.online ? (r.activeDeliveries ? `${r.activeDeliveries} job${r.activeDeliveries > 1 ? 's' : ''}` : 'Free') : 'Offline')} />
        <Link to="/ops/riders" className="btn btn-quiet">
          Dispatch <Icon name="chevronRight" />
        </Link>
      </Sheet>

      <Sheet open={sheet === 'cash'} title="Cash on the road" onClose={() => setSheet(null)}>
        <div className="dv-kv">
          <div>
            <span className="dv-label">COD on the road</span>
            <Fig text={peso(k?.codOnRoadCentavos ?? 0)} size="md" />
          </div>
          <div>
            <span className="dv-label">Riders holding cash</span>
            <Fig text={count(riderList.filter((r) => r.cashOnHandCentavos > 0).length)} size="md" />
          </div>
        </div>
        <RiderRows riders={riderList.filter((r) => r.cashOnHandCentavos > 0).sort((a, b) => b.cashOnHandCentavos - a.cashOnHandCentavos)} value={(r) => peso(r.cashOnHandCentavos)} empty="No rider is holding cash" />
        <Link to="/ops/payments" className="btn btn-quiet">
          Payments &amp; COD <Icon name="chevronRight" />
        </Link>
      </Sheet>

      <Sheet open={sheet === 'alerts'} title="Needs you" onClose={() => setSheet(null)}>
        {overview.data ? <AlertsOrClear alerts={alerts} /> : <Loading label="Loading alerts" />}
      </Sheet>
    </>
  );
}

type MapHealth = Awaited<ReturnType<typeof api.mapHealth>>;

/** Maps & routing at a glance: one row per service, a status dot on its icon, one short value. */
function HealthRows({ h, error }: { h: MapHealth | undefined; error: boolean }) {
  if (error) return <div className="dv-quiet">Can't reach the map services</div>;
  if (!h) return <Loading label="Checking the map services" />;
  const own = (x: boolean) => (x ? 'ok' : 'warn');
  const rows: { key: string; icon: IconName; name: string; tone: string; value: ReactNode }[] = [
    { key: 'routing', icon: 'swap', name: 'Road routing', tone: h.routing.status === 'ok' ? 'ok' : 'bad', value: h.routing.status === 'ok' ? <Fig text={`${h.routing.latencyMs} ms`} size="xs" animate={false} /> : <span className="dv-chip bad">Down</span> },
    { key: 'tiles', icon: 'globe', name: 'Map tiles', tone: own(h.basemap?.source === 'self-hosted'), value: h.basemap?.source === 'self-hosted' ? <Fig text={`${h.basemap.sizeMb} MB`} size="xs" animate={false} /> : <span className="dv-chip">Borrowed</span> },
    { key: 'fonts', icon: 'list', name: 'Map labels', tone: own(h.fonts === 'self-hosted'), value: <span className="dv-chip">{h.fonts === 'self-hosted' ? 'Ours' : 'Borrowed'}</span> },
  ];
  return (
    <div className="dv-health">
      <div className="dv-health-h">
        <span className="dv-label">Maps &amp; routing</span>
        <Link to="/ops/maps" className="dv-link">
          Details <Icon name="chevronRight" />
        </Link>
      </div>
      {rows.map((r) => (
        <Link key={r.key} to="/ops/maps" className={r.tone} aria-label={`${r.name}: ${r.tone === 'ok' ? 'healthy' : r.tone === 'bad' ? 'down' : 'fallback'}`}>
          <span className="ic" aria-hidden="true">
            <Icon name={r.icon} />
          </span>
          <span className="nm">{r.name}</span>
          {r.value}
        </Link>
      ))}
    </div>
  );
}

function AlertsOrClear({ alerts }: { alerts: OpsOverview['alerts'] }) {
  if (!alerts.length)
    return (
      <div className="dv-quiet" style={{ padding: 'var(--pc-space-32)' }}>
        <Icon name="check" />
        Nothing needs you
      </div>
    );
  return <Alerts alerts={alerts} />;
}

function MiniBars({ values }: { values: number[] }) {
  const v = values.slice(-14);
  if (!v.length) return null;
  const max = Math.max(1, ...v);
  return (
    <span className="dv-bars" aria-hidden="true">
      {v.map((x, j) => (
        <i key={j} className={j === v.length - 1 ? 'hot' : ''} style={{ height: `${Math.max(7, (x / max) * 100)}%`, ['--j' as string]: j }} />
      ))}
    </span>
  );
}

function DayBars({ days }: { days: { day: string; gmvCentavos: number }[] }) {
  if (!days.length) return <div className="dv-quiet">No days yet</div>;
  const max = Math.max(1, ...days.map((d) => d.gmvCentavos));
  return (
    <>
      <div className="dv-daybars" role="img" aria-label={`Sales per day: ${days.map((d) => `${shortDay(d.day)} ${peso(d.gmvCentavos)}`).join(', ')}`}>
        {days.map((d, j) => (
          <div key={d.day} title={`${dayLabel(d.day)} · ${peso(d.gmvCentavos)}`}>
            <i style={{ height: `${Math.max(2, (d.gmvCentavos / max) * 100)}%`, ['--j' as string]: j }} />
          </div>
        ))}
      </div>
      <div className="row between dv-sub">
        <span>{shortDay(days[0]?.day ?? '')}</span>
        <span>{shortDay(days[days.length - 1]?.day ?? '')}</span>
      </div>
    </>
  );
}

function RiderRows({ riders, value, empty = 'No riders yet' }: { riders: OpsRider[]; value(r: OpsRider): string; empty?: string }) {
  if (!riders.length) return <div className="dv-quiet">{empty}</div>;
  return (
    <section className="card dv-card" style={{ paddingTop: 'var(--pc-space-8)', paddingBottom: 'var(--pc-space-8)' }}>
      <div className="dv-rows">
        {riders.map((r) => (
          <div key={r.userId}>
            <span className="row">
              <span className={`dv-pulse${r.online ? '' : ' off'}`} style={r.online ? undefined : { background: 'var(--dv-hair-strong)' }} aria-hidden="true" />
              <b className="t-label">{r.name}</b>
            </span>
            <Fig text={value(r)} size="xs" animate={false} />
          </div>
        ))}
      </div>
    </section>
  );
}

export function LiveOrdersPage() {
  useTitle('Live orders');
  const [shop, setShop] = useState<string | null>(null);
  const [lateOnly, setLateOnly] = useState(false);
  const [q, setQ] = useState('');
  const board = useOpsBoard();
  const now = useNow(1000);
  const norm = useMemo(() => normaliseBoard(board.data), [board.data]);
  const view = useMemo(() => filterBoard(norm, { shop, lateOnly, q }), [norm, shop, lateOnly, q]);
  return (
    <>
      <div className="top">
        <div className="grow stack s4">
          <h1 className="t-title">Live orders</h1>
          <span className="t-monocap muted">{updatedAgo(board.dataUpdatedAt, now)} · REFRESHES EVERY 5 S</span>
        </div>
        <label className="search">
          <span className="sr-only">Search orders</span>
          <svg className="ico" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M10.5 17a6.5 6.5 0 100-13 6.5 6.5 0 000 13zM15.5 15.5L20 20" />
          </svg>
          <input type="search" placeholder="Order code, shop or address" value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
      </div>
      <section className="card panel" aria-label="Board">
        <BoardFilters shopNames={boardShopNames(norm)} shop={shop} setShop={setShop} lateOnly={lateOnly} setLateOnly={setLateOnly} late={lateCount(norm)} />
        {board.isError ? <ErrorState error={board.error} onRetry={() => void board.refetch()} /> : board.isLoading ? <Loading label="Loading live orders" rows={3} /> : <LiveBoard board={view} />}
      </section>
    </>
  );
}
